import React, { useCallback, useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { defaultRange, downloadReportFile, getPlatformReport, type PlatformReport } from '../../../api/reportingApi';
import { fmtMoney } from '../../../api/billingApi';
import { S } from './adminPageStyles';

/** Master reporting (SRS #80): users, location, adoption, revenue, platform performance for a period. */

const label = (k: string) => k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const AdminReportsPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [range, setRange] = useState(defaultRange());
  const [r, setR] = useState<PlatformReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    getPlatformReport(range).then(setR).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load')).finally(() => setLoading(false));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const tile = (l: string, v: React.ReactNode, hint?: string) => (
    <div key={l} style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: '12px 14px', background: 'var(--ds-surface)' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ds-text)' }}>{v}</div>
      <div style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>{l}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--ds-text-faint)' }}>{hint}</div>}
    </div>
  );
  const kvTable = (title: string, d: Record<string, unknown>) => (
    <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)' }}>{title}</div>
      <table style={S.table}><tbody>
        {Object.entries(d).filter(([, v]) => !Array.isArray(v)).map(([k, v]) => (
          <tr key={k}><td style={S.td}>{label(k)}</td><td style={{ ...S.td, ...S.mono, textAlign: 'right' }}>{v === null || v === undefined ? '—' : String(v)}</td></tr>
        ))}
      </tbody></table>
    </div>
  );

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Reports" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Platform report</h2>
        <p style={S.sub}>Users, where they are, what they used, what it earned and how the pipeline behaved — for the period. Every figure is period-scoped except totals, MRR and the open-issue counts.</p>
        <div style={S.toolbar}>
          <input type="date" style={S.input} value={range.since} onChange={(e) => setRange((x) => ({ ...x, since: e.target.value }))} />
          <span style={S.muted}>to</span>
          <input type="date" style={S.input} value={range.until} onChange={(e) => setRange((x) => ({ ...x, until: e.target.value }))} />
          <button style={S.btn} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          <button style={S.btn} onClick={() => downloadReportFile('platform', 'csv', range).catch((e) => setError(e.message))}>CSV</button>
          <button style={S.primary} onClick={() => downloadReportFile('platform', 'pdf', range).catch((e) => setError(e.message))}>PDF report</button>
        </div>
        {error && <div style={S.error}>{error}</div>}
        {r && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
              {tile('Users', r.users.total, `${r.users.new} new · ${r.users.active} active in period`)}
              {tile('Collected', fmtMoney(r.revenue.collected, 'USD'), `${r.revenue.payments} payments · ${r.revenue.failed} failed`)}
              {tile('MRR (estimate)', fmtMoney(r.revenue.mrr_estimate, 'USD'), 'active paid plans, annual ÷ 12')}
              {tile('Conversions', r.revenue.conversions, `${r.revenue.trials_started} trials started`)}
              {tile('Searches', r.adoption.searches, `${r.adoption.chat_messages} chat messages`)}
              {tile('Ingestion success', r.performance.run_success_rate == null ? '—' : `${r.performance.run_success_rate}%`, `${r.performance.ingestion_runs} runs · ${r.performance.open_sync_alerts} open alerts`)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 14, marginBottom: 16 }}>
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Users by country</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={r.users.by_country.slice(0, 10)} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--ds-border)" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="country" width={90} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="users" fill="var(--ds-brand-blue)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Adoption in period</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={Object.entries(r.adoption).map(([k, v]) => ({ name: label(k), value: v }))} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--ds-border)" />
                    <XAxis dataKey="name" interval={0} angle={-35} textAnchor="end" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="var(--ds-success)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 14 }}>
              {kvTable('Users', r.users)}
              {kvTable('Revenue', r.revenue)}
              {kvTable('Platform performance', r.performance)}
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)' }}>Users by plan</div>
                <table style={S.table}><tbody>
                  {r.users.by_plan.map((p) => <tr key={`${p.plan}-${p.status}`}><td style={S.td}>{label(p.plan)} <span style={S.chip}>{p.status}</span></td><td style={{ ...S.td, ...S.mono, textAlign: 'right' }}>{p.users}</td></tr>)}
                </tbody></table>
              </div>
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)' }}>Most active organisations</div>
                <table style={S.table}>
                  <thead><tr><th style={S.th}>Organisation</th><th style={S.th}>Country</th><th style={S.th}>Logins</th><th style={S.th}>Users</th></tr></thead>
                  <tbody>
                    {r.top_organisations.length === 0 && <tr><td style={S.td} colSpan={4}><span style={S.muted}>No activity in the period.</span></td></tr>}
                    {r.top_organisations.map((o) => <tr key={`${o.organisation}-${o.country}`}><td style={S.td}>{o.organisation}</td><td style={S.td}>{o.country || '—'}</td><td style={{ ...S.td, ...S.mono }}>{o.logins}</td><td style={{ ...S.td, ...S.mono }}>{o.users}</td></tr>)}
                  </tbody>
                </table>
              </div>
              <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)' }}>Users by industry</div>
                <table style={S.table}><tbody>
                  {r.users.by_industry.map((i) => <tr key={i.industry}><td style={S.td}>{i.industry}</td><td style={{ ...S.td, ...S.mono, textAlign: 'right' }}>{i.users}</td></tr>)}
                </tbody></table>
              </div>
            </div>
          </>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminReportsPage;
